import React, { useState, useEffect } from "react";
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import './AutoSuggestW3W.scss';
import what3WordsIcon from '../../../../assets/images/what3words.png';
import { ServiceEndPoints } from "../../../../utils/constants/ApiConstant";
import { GET } from '../../../../api/axios';

function AutoSuggestW3W(props) {
  const {
    setFieldValue,
    formikValues,
    isDisabled,
    formField: {
      what3Words,
      w3WCoordinates
    }
  } = props;
  const [value, setValue] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [selectedValidW3W, setSelectedValidW3W] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvalidError, setShowInvalidError] = useState(false);

  useEffect(() => {
    setValue(formikValues?.what3Words || "");
    setSelectedValidW3W(formikValues?.what3Words);
  }, [formikValues?.what3Words]);

  useEffect(() => {
    const cleanedValue = (value || '').replace(/[^A-Za-z./]/g, '');
    const plainWords = cleanedValue.replace(/^\/+/, '');
    const dotCount = (plainWords.match(/\./g) || []).length;

    // Only start autosuggest after user has typed at least two dots, e.g. word.word.
    if (!plainWords || plainWords.length < 3 || dotCount < 2) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const debounceTimer = setTimeout(async () => {
      try {
        setLoading(true);
        setShowInvalidError(false);
        const inputValue = plainWords.startsWith('///') ? plainWords : `///${plainWords}`;
        const autosuggestUrl = `${ServiceEndPoints.getWhat3Words}/autosuggest?input=${encodeURIComponent(inputValue)}&clip-to-country=GB,JE,GG&language=en`;
        const response = await GET(autosuggestUrl);
        const suggestions = response?.data?.suggestions || response?.data || [];
        setOptions(Array.isArray(suggestions) ? suggestions : []);
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [value]);

  const onChange = (e) => {
    let cleanedValue = e.target.value.replace(/[^A-Za-z./]/g, '');
    if (cleanedValue && !cleanedValue.startsWith('///')) {
      cleanedValue = `///${cleanedValue.replace(/^\/+/, '')}`;
    }

    setValue(cleanedValue);
    setIsValid(false);
    setShowInvalidError(false);
    if(cleanedValue ==='')
    {
      setValue("");
      setFieldValue('what3Words', "");
      setFieldValue('w3WCoordinates', {});
      setOptions([]);
    }
  };

  const onBlur = () => {
    if(!isValid && !selectedValidW3W){
        const hasTypedWord = !!(value || '').replace(/^\/+/, '').trim();
        setValue("");
        setFieldValue('what3Words', "");
        setFieldValue('w3WCoordinates', {});
        setShowInvalidError(hasTypedWord);
      }
      setSelectedValidW3W('');
  };

  const onFocus = () => {
    if (!value) {
      setValue('///');
      setShowInvalidError(false);
    }
  };

  const onValidW3WSelection = async (selectedOption) => {
    if (!selectedOption?.words) {
      return;
    }

    try {
      setLoading(true);
      const convertUrl = `${ServiceEndPoints.getWhat3Words}/convert-to-coordinates?words=${encodeURIComponent(selectedOption.words)}&format=json`;
      const response = await GET(convertUrl);
      const coordinates = response?.data?.coordinates || response?.data?.geo || {};

      setFieldValue('what3Words', selectedOption.words);
      setFieldValue('w3WCoordinates', coordinates || {});
      setValue(`///${selectedOption.words}`);
      setIsValid(true);
      setSelectedValidW3W(selectedOption.words);
      setShowInvalidError(false);
    } catch (error) {
      setIsValid(false);
      setShowInvalidError(true);
      setFieldValue('w3WCoordinates', {});
    } finally {
      setLoading(false);
    }
  }

  const formatWords = (words = '') => {
    if (!words) {
      return '';
    }
    return words.startsWith('///') ? words : `///${words}`;
  };

  return (
    <>
      <Autocomplete
        freeSolo
        className="w3w-autocomplete"
        options={options}
        loading={loading}
        disabled={isDisabled}
        disableClearable
        forcePopupIcon={false}
        filterOptions={(x) => x}
        getOptionLabel={(option) => (typeof option === 'string' ? option : formatWords(option?.words))}
        inputValue={value}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            let cleaned = (newInputValue || '').replace(/[^A-Za-z./]/g, '');
            if (cleaned && !cleaned.startsWith('///')) {
              cleaned = `///${cleaned.replace(/^\/+/, '')}`;
            }
            setValue(cleaned);
            setIsValid(false);
            setShowInvalidError(false);
          }
        }}
        onChange={(event, selectedOption) => onValidW3WSelection(selectedOption)}
        renderOption={(option) => (
          <div className="w3w-option-item">
            <div className="w3w-option-words">
              <span className="w3w-option-slashes">///</span>
              <span>{option?.words || ''}</span>
            </div>
            {option?.nearestPlace ? (
              <div className="w3w-option-nearest-place">
                near {option.nearestPlace}
              </div>
            ) : null}
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className="w3w-input-field"
            id="w3w"
            autoComplete="off"
            placeholder="Start typing what3words e.g. ///pretty.needed.chill"
            type="text"
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={isDisabled}
            error={showInvalidError}
            helperText={showInvalidError ? 'No valid what3words address found' : ''}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <div className="w3w-input-adornment">
                  {isValid && !loading ? <CheckCircleOutlineIcon className="w3w-valid-icon" /> : null}
                  {loading ? <CircularProgress color="inherit" size={16} className="w3w-spinner" /> : null}
                  <div className="w3w-icon-box">
                    <img src={what3WordsIcon} alt="what3words" className="w3w-icon" />
                  </div>
                </div>
              ),
            }}
          />
        )}
      >
      </Autocomplete>
    </>
  );
}

export default AutoSuggestW3W;
